import { useState, useEffect, useRef } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable, SortableHeader } from '../ui/data-table'
import { Badge } from '../ui/badge'
import { apiClient } from '../../lib/api'
import { useScrollRestoration } from '../../hooks/useScrollRestoration'
import { formatDateTimeCompact } from '../../lib/utils'
import type { Transaction } from '../../types'

interface TransactionsTableProps {
  isActive?: boolean
}

export function TransactionsTable({ isActive = true }: TransactionsTableProps) {
  useScrollRestoration('admin-transactions-table', isActive)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [pageCount, setPageCount] = useState(0)

  // Use a ref to track previous pagination to avoid unnecessary fetches
  const prevPaginationRef = useRef({ pageIndex: 0, pageSize: 10 })

  useEffect(() => {
    // Only load if pagination values actually changed
    if (
      prevPaginationRef.current.pageIndex !== pagination.pageIndex ||
      prevPaginationRef.current.pageSize !== pagination.pageSize
    ) {
      prevPaginationRef.current = pagination
      loadTransactions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  // Load transactions on initial mount
  useEffect(() => {
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadTransactions = async () => {
    try {
      setLoading(true)
      const page = pagination.pageIndex + 1 // API uses 1-based pages
      const data = await apiClient.getAdminTransactions(page, pagination.pageSize)
      setTransactions(data.transactions)
      if (data.pagination) {
        setPageCount(data.pagination.total_pages)
      }
    } catch (error) {
      console.error('Failed to load transactions:', error)
    } finally {
      setLoading(false)
    }
  }


  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default'
      case 'pending':
        return 'secondary'
      case 'failed':
        return 'destructive'
      case 'refunded':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'translation':
        return 'default'
      case 'subscription':
        return 'default'
      case 'refund':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount)
  }

  const columns: ColumnDef<Transaction>[] = [
    {
      accessorKey: 'id',
      header: ({ column }) => <SortableHeader column={column}>Transaction ID</SortableHeader>,
      cell: ({ row }) => (
        <div className="transaction-id-cell font-mono text-xs">
          {row.original.id.substring(0, 8)}...
        </div>
      ),
    },
    {
      accessorKey: 'user_id',
      header: ({ column }) => <SortableHeader column={column}>User ID</SortableHeader>,
      cell: ({ row }) => (
        <div className="user-id-cell font-mono text-xs">
          {row.original.user_id.substring(0, 8)}...
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>,
      cell: ({ row }) => (
        <Badge variant={getTypeBadgeVariant(row.original.type)} className="type-badge">
          {row.original.type}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      cell: ({ row }) => (
        <Badge variant={getStatusBadgeVariant(row.original.status)} className="status-badge">
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => <SortableHeader column={column}>Amount</SortableHeader>,
      cell: ({ row }) => (
        <div className="amount-cell text-sm font-medium">
          {formatAmount(row.original.amount, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'tokens_used',
      header: 'Tokens',
      cell: ({ row }) => (
        <div className="tokens-cell text-sm">
          {row.original.tokens_used ? (
            <div className="flex flex-col">
              <span>{row.original.tokens_used.toLocaleString()}</span>
              {row.original.free_tokens_applied && row.original.free_tokens_applied > 0 && (
                <span className="text-xs text-muted-foreground">
                  ({row.original.free_tokens_applied.toLocaleString()} free)
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="description-cell max-w-xs">
          <div className="text-sm text-muted-foreground line-clamp-2">
            {row.original.description || '—'}
          </div>
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'payment_provider',
      header: 'Provider',
      cell: ({ row }) => (
        <div className="provider-cell text-sm">
          {row.original.payment_provider || '—'}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.created_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
  ]

  if (loading) {
    return (
      <div className="transactions-table-loading flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading transactions...</p>
      </div>
    )
  }

  return (
    <div className="transactions-table-container space-y-4">
      {/* Data Table */}
      <DataTable
        columns={columns}
        data={transactions}
        searchColumn="id"
        searchPlaceholder="Search by transaction ID or user ID..."
        enableRowSelection={false}
        enableColumnVisibility={true}
        enablePagination={true}
        pageSize={10}
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={setPagination}
      />
    </div>
  )
}

